import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';

const FooterContainer = styled(Box)({
  background: 'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.95) 20%)',
  borderTop: '2px solid rgba(8, 145, 178, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #0891b2, #64748b, #10b981, transparent)',
    animation: 'scan 3s linear infinite',
  },
  '@keyframes scan': {
    '0%': {
      transform: 'translateX(-100%)',
    },
    '100%': {
      transform: 'translateX(100%)',
    },
  },
});

const SectionTitle = styled(Typography)({
  fontFamily: '"Rajdhani", sans-serif',
  fontSize: '1.3rem',
  fontWeight: 700,
  color: '#0891b2',
  marginBottom: '20px',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  position: 'relative',
  paddingBottom: '10px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '60px',
    height: '2px',
    background: 'linear-gradient(90deg, #0891b2, #64748b)',
  },
});

const StyledLink = styled(Link)({
  color: '#E0E0E0',
  textDecoration: 'none',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '0.95rem',
  display: 'block',
  marginBottom: '4px',
  padding: '6px 0',
  '@media (max-width: 600px)': {
    padding: '10px 0',
    marginBottom: 0,
  },
  transition: 'all 0.3s ease',
  position: 'relative',
  paddingLeft: '0',
  cursor: 'pointer',
  '&::before': {
    content: '"▸"',
    position: 'absolute',
    left: '-20px',
    opacity: 0,
    transition: 'all 0.3s ease',
    color: '#0891b2',
  },
  '&:hover': {
    color: '#0891b2',
    paddingLeft: '20px',
    '&::before': {
      left: '0',
      opacity: 1,
    },
  },
});

const SocialIconButton = styled('a')({
  '&:hover:not(:active)': { transform: 'scale(1.1) rotate(5deg)' },
  '&:active': { transform: 'scale(0.9)' },
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '45px',
  height: '45px',
  border: '2px solid rgba(8, 145, 178, 0.5)',
  color: '#0891b2',
  marginRight: '12px',
  marginBottom: '12px',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '0',
    height: '0',
    background: 'radial-gradient(circle, rgba(8, 145, 178, 0.3), transparent)',
    transform: 'translate(-50%, -50%)',
    transition: 'all 0.3s ease',
  },
  '&:hover': {
    borderColor: '#0891b2',
    boxShadow: '0 0 20px rgba(8, 145, 178, 0.6), inset 0 0 20px rgba(8, 145, 178, 0.1)',
    transform: 'translateY(-3px)',
    '&::before': {
      width: '100%',
      height: '100%',
    },
  },
});

const ContactInfo = styled(Typography)({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '0.95rem',
  color: '#E0E0E0',
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  '& .icon': {
    marginRight: '10px',
    color: '#0891b2',
    fontSize: '1.2rem',
  },
});

const CopyrightText = styled(Typography)({
  fontFamily: '"Rajdhani", sans-serif',
  fontSize: '0.9rem',
  color: 'rgba(255, 255, 255, 0.6)',
  textAlign: 'center',
  paddingTop: '30px',
  borderTop: '1px solid rgba(8, 145, 178, 0.2)',
  letterSpacing: '0.05em',
});

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const FuturisticFooter = () => {
  return (
    <FooterContainer sx={{ py: 8, mt: 'auto' }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} sm={6} md={3}>
            <SectionTitle variant="h6" as="h2">VITNOLOGY</SectionTitle>
            <Typography
              sx={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontSize: '0.9rem',
                color: '#E0E0E0',
                mb: 3,
                lineHeight: 1.8,
              }}
            >
              Microsoft Partner for Dynamics 365, managed IT, and custom development. One team for your whole technology stack.
            </Typography>
            <ContactInfo>
              <span className="icon">📧</span>
              support@vitnology.com
            </ContactInfo>
            <ContactInfo>
              <span className="icon">📞</span>
              (503) 908-4405
            </ContactInfo>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <SectionTitle variant="h6" as="h2">Quick Links</SectionTitle>
            <StyledLink to="/">
              Home
            </StyledLink>
            <StyledLink to="/about">
              About Us
            </StyledLink>
            <StyledLink to="/how-we-work">
              How We Work
            </StyledLink>
            <StyledLink to="/blog">
              Resources
            </StyledLink>
            <StyledLink to="/contact">
              Contact Us
            </StyledLink>
            <StyledLink to="/general-consultation">
              Free Consultation
            </StyledLink>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={3}>
            <SectionTitle variant="h6" as="h2">Services</SectionTitle>
            <StyledLink to="/services/dynamics-365">
              Microsoft Dynamics 365
            </StyledLink>
            <StyledLink to="/services/microsoft-365-azure">
              Microsoft 365 & Azure
            </StyledLink>
            <StyledLink to="/services/managed-it-services">
              Managed IT & Help Desk
            </StyledLink>
            <StyledLink to="/services/cybersecurity">
              Cybersecurity
            </StyledLink>
            <StyledLink to="/services/cloud-migration">
              Cloud & Azure
            </StyledLink>
            <StyledLink to="/services/software-solutions">
              Software & Web Development
            </StyledLink>
          </Grid>

          {/* Social Media */}
          <Grid item xs={12} sm={6} md={3}>
            <SectionTitle variant="h6" as="h2">Connect</SectionTitle>
            <Typography
              sx={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontSize: '0.9rem',
                color: '#E0E0E0',
                mb: 2,
              }}
            >
              Follow us on social media for updates and tech insights.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              <SocialIconButton
                href="https://instagram.com/vitnology"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
              >
                <InstagramIcon />
              </SocialIconButton>
              <SocialIconButton
                href="https://linkedin.com/company/vitnology"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on LinkedIn"
              >
                <LinkedInIcon />
              </SocialIconButton>
              <SocialIconButton
                href="https://facebook.com/vitnology"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
              >
                <FacebookIcon />
              </SocialIconButton>
              <SocialIconButton
                href="https://x.com/vitnology"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on X (formerly Twitter)"
              >
                <XIcon />
              </SocialIconButton>
            </Box>
          </Grid>
        </Grid>

        <CopyrightText sx={{ mt: 5 }}>
          © {new Date().getFullYear()} VITNOLOGY. ALL RIGHTS RESERVED. | MICROSOFT PARTNER
        </CopyrightText>
      </Container>

      {/* Scanning line effect */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, #0891b2, transparent)',
          animation: 'scanBottom 4s linear infinite',
          '@keyframes scanBottom': {
            '0%': {
              transform: 'translateX(-100%)',
            },
            '100%': {
              transform: 'translateX(100%)',
            },
          },
        }}
      />
    </FooterContainer>
  );
};

export default FuturisticFooter;

